import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  EmptyState,
  LoadingState,
  SectionHeader,
  Typography,
} from '../../common';
import {
  DivisionTable,
  LeagueSeasonSelect,
  MatchCardModal,
  ResultRow,
  SeasonSnapshot,
} from '../../leagues/LeagueMatchViews';
import { ClubLeaguePlayerAnalysis } from './ClubLeaguePlayerAnalysis';
import {
  fetchClubLeagueLink,
  fetchLinkedClubSeason,
  fetchMatchCard,
  fetchMiddlesexSeasons,
  fetchViewerLeagueAppearances,
} from '../../../services/leagueService';
import { pickDefaultClubLeagueTeam, sortLeagueFixturesUpcomingFirst } from '../../../utils/leagueTeamSelection';

const FIXTURE_PREVIEW_COUNT = 5;

export const ClubLeagueTab = ({ clubId, canManage, currentUserId, viewerGender = null }) => {
  const navigate = useNavigate();
  const [link, setLink] = useState(null);
  const [linkLoading, setLinkLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [startYear, setStartYear] = useState(null);
  const [teamChoice, setTeamChoice] = useState(null);
  const [selectedFixture, setSelectedFixture] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [playedFixtureIds, setPlayedFixtureIds] = useState(() => new Set());
  const [viewerTeamIds, setViewerTeamIds] = useState([]);
  const [viewerReady, setViewerReady] = useState(false);
  const [showAllFixtures, setShowAllFixtures] = useState(false);

  const reloadLink = useCallback(async () => {
    const next = await fetchClubLeagueLink(clubId);
    setLink(next);
    return next;
  }, [clubId]);

  useEffect(() => {
    let cancelled = false;
    setLinkLoading(true);
    reloadLink()
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLinkLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadLink]);

  useEffect(() => {
    if (link?.status !== 'approved') {
      setSnapshot(null);
      setSeasons([]);
      setStartYear(null);
      return undefined;
    }
    let cancelled = false;
    fetchMiddlesexSeasons()
      .then((rows) => {
        if (cancelled) return;
        setSeasons(rows);
        setStartYear((prev) => (rows.some((row) => row.start_year === prev) ? prev : rows[0]?.start_year ?? null));
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [link]);

  useEffect(() => {
    if (link?.status !== 'approved' || !currentUserId) {
      setPlayedFixtureIds(new Set());
      setViewerTeamIds([]);
      setViewerReady(true);
      return undefined;
    }
    let cancelled = false;
    setViewerReady(false);
    fetchViewerLeagueAppearances(clubId, currentUserId)
      .then(({ fixtureIds, teamIds }) => {
        if (cancelled) return;
        setPlayedFixtureIds(new Set(fixtureIds));
        setViewerTeamIds(teamIds);
      })
      .catch(() => {
        if (cancelled) return;
        setPlayedFixtureIds(new Set());
        setViewerTeamIds([]);
      })
      .finally(() => {
        if (!cancelled) setViewerReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [clubId, currentUserId, link]);

  useEffect(() => {
    if (link?.status !== 'approved' || startYear == null) {
      return undefined;
    }
    let cancelled = false;
    fetchLinkedClubSeason(link.league_club_id, startYear)
      .then((season) => {
        if (cancelled) return;
        setSnapshot(season);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [link, startYear]);

  const teamsById = useMemo(
    () => new Map((snapshot?.allTeams ?? []).map((team) => [team.id, team])),
    [snapshot],
  );
  const team = useMemo(() => {
    if (!snapshot || !viewerReady) return null;
    return pickDefaultClubLeagueTeam({
      teams: snapshot.teams,
      divisions: snapshot.divisions,
      competitions: snapshot.competitions,
      viewerTeamIds,
      viewerGender,
      previousTeam: teamChoice,
    });
  }, [snapshot, viewerReady, viewerTeamIds, viewerGender, teamChoice]);
  const viewerOnTeam = Boolean(team && viewerTeamIds.includes(team.id));
  const division = (snapshot?.divisions ?? []).find((row) => row.id === team?.division_id) ?? null;
  const competition = (snapshot?.competitions ?? []).find((row) => row.id === division?.competition_id) ?? null;
  const fixtures = useMemo(() => {
    if (!snapshot || !team) return [];
    return sortLeagueFixturesUpcomingFirst(
      snapshot.fixtures.filter(
        (fixture) => fixture.home_team_id === team.id || fixture.away_team_id === team.id,
      ),
    );
  }, [snapshot, team]);
  const teamId = team?.id;
  useEffect(() => {
    setShowAllFixtures(false);
  }, [teamId, startYear]);
  const visibleFixtures = showAllFixtures ? fixtures : fixtures.slice(0, FIXTURE_PREVIEW_COUNT);
  const divisionStandings = useMemo(
    () => (snapshot?.standings ?? []).filter((row) => row.division_id === team?.division_id),
    [snapshot, team],
  );

  const openFixture = async (fixture) => {
    setSelectedFixture({ fixture, teamId: team.id });
    try {
      setSelectedCard(await fetchMatchCard(fixture.id));
    } catch {
      setSelectedCard({ lineups: [], rubbers: [], players: [] });
    }
  };

  if (linkLoading) {
    return <LoadingState message="Loading league tab…" />;
  }

  if (!link || link.status !== 'approved') {
    return (
      <EmptyState
        title={link?.status === 'pending' ? 'Link pending approval' : 'No county club linked'}
        description="Club hosts and admins can request a Middlesex club link from Club management → League squad."
      />
    );
  }

  if (!snapshot || !viewerReady) {
    return <LoadingState message="Loading linked teams…" />;
  }

  const meta = [competition?.label, division?.name].filter(Boolean).join(' · ');
  const clubTeams = snapshot.teams ?? [];
  const darkSelectClass =
    'w-full min-h-[44px] rounded-xl border border-white/20 bg-white/10 px-3 py-2 text-sm font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50';

  return (
    <div className="space-y-6">
      {error ? <Typography variant="warning">{error}</Typography> : null}

      {canManage && (
        <div className="rounded-xl border border-border-medium border-l-4 border-l-primary bg-surface-subtle/50 p-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <Typography variant="caption" className="font-medium uppercase tracking-wide text-text-muted">
                Admin
              </Typography>
              <Typography variant="caption" className="mt-0.5 block text-text-secondary">
                Link county names to club members
              </Typography>
            </div>
            <Button
              variant="primary"
              size="small"
              className="w-full sm:w-auto shrink-0"
              onClick={() => navigate(`/club/${clubId}/league-squad`, { state: { returnToDashboard: true } })}
            >
              Manage squad
            </Button>
          </div>
        </div>
      )}

      <SeasonSnapshot
        leagueName={competition?.label}
        divisionName={division?.name}
        header={(
          <div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <LeagueSeasonSelect
                seasons={seasons}
                value={startYear}
                onChange={setStartYear}
                variant="dark"
                className="w-full sm:w-44"
              />
              {clubTeams.length > 1 ? (
                <>
                  <label htmlFor="league-team-select" className="sr-only">Team</label>
                  <select
                    id="league-team-select"
                    value={team?.id ?? ''}
                    onChange={(event) => {
                      const next = clubTeams.find((item) => item.id === event.target.value);
                      setTeamChoice(next
                        ? { id: next.id, name: next.name, source_team_id: next.source_team_id }
                        : null);
                    }}
                    className={`${darkSelectClass} sm:flex-1 sm:max-w-md`}
                  >
                    {clubTeams.map((item) => (
                      <option key={item.id} value={item.id} className="text-text-primary">
                        {item.name}
                      </option>
                    ))}
                  </select>
                </>
              ) : clubTeams.length === 1 ? (
                <h2 className="truncate text-lg font-bold leading-tight text-white sm:text-xl">{team.name}</h2>
              ) : null}
            </div>
            {meta ? <p className="mt-1.5 text-sm text-white/70">{meta}</p> : null}
          </div>
        )}
      />

      {viewerOnTeam && snapshot.season?.id ? (
        <ClubLeaguePlayerAnalysis
          clubId={clubId}
          currentUserId={currentUserId}
          teamId={team.id}
          seasonId={snapshot.season.id}
          teamsById={teamsById}
          onOpenFixture={openFixture}
        />
      ) : null}

      {!team ? (
        <EmptyState title="No teams this season" description="This club has no Middlesex teams in the selected season." />
      ) : (
      <div className="space-y-6">
        <div>
          <SectionHeader title="Table" size="md" />
          <Card size="small" className="mt-3 overflow-x-auto p-0 sm:p-0">
            <DivisionTable standings={divisionStandings} highlightTeamId={team.id} teamsById={teamsById} divisionName={division?.name} />
          </Card>
        </div>
        <div className="space-y-2">
          <SectionHeader title="Fixtures" size="md" />
          {fixtures.length === 0 ? (
            <EmptyState title="No fixtures" description="Nothing for this team yet." />
          ) : (
            <>
              {visibleFixtures.map((fixture) => (
                <ResultRow
                  key={fixture.id}
                  fixture={fixture}
                  teamId={team.id}
                  teamsById={teamsById}
                  onOpen={() => openFixture(fixture)}
                  viewerPlayed={playedFixtureIds.has(fixture.id)}
                />
              ))}
              {fixtures.length > FIXTURE_PREVIEW_COUNT ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="small"
                  className="w-full"
                  aria-expanded={showAllFixtures}
                  onClick={() => setShowAllFixtures((prev) => !prev)}
                >
                  {showAllFixtures
                    ? 'Show fewer'
                    : `Show all ${fixtures.length} fixtures`}
                </Button>
              ) : null}
            </>
          )}
        </div>
      </div>
      )}

      {selectedFixture && (
        <MatchCardModal
          fixture={selectedFixture.fixture}
          card={selectedCard}
          teamsById={teamsById}
          competitionLabel={
            (snapshot?.competitions ?? []).find((row) => row.id === selectedFixture.fixture.competition_id)?.label
            ?? competition?.label
          }
          divisionName={
            (snapshot?.divisions ?? []).find((row) => row.id === selectedFixture.fixture.division_id)?.name
            ?? division?.name
          }
          ourTeamId={selectedFixture.teamId}
          onClose={() => {
            setSelectedFixture(null);
            setSelectedCard(null);
          }}
        />
      )}
    </div>
  );
};
